import * as React from "react";

import { cn } from "../utils";
import { Icon } from "../icon";
import { cursorElementVariants } from "../custom-cursor";
import type { PlayButtonProps } from "./play-button.types";

/**
 * Play Button Component
 *
 * V&A Waterfront Design System - Play Button
 * Figma: https://www.figma.com/design/AfQjJbsLFwNJO6qGxwPFYh/V-A-Waterfront---Website---Design---Dev-File?node-id=2097-47262
 *
 * Reuses `cursorElementVariants({ type: "play" })` so the static button
 * stays visually in sync with the custom-cursor "play" variant.
 *
 * @example
 * <PlayButton onClick={() => video.play()} />
 */
const PlayButton = React.forwardRef<HTMLButtonElement, PlayButtonProps>(
  (
    { className, "aria-label": ariaLabel = "Play video", disabled, ...props },
    ref,
  ) => (
    <button
      ref={ref}
      data-slot="play-button"
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      className={cn(
        cursorElementVariants({ type: "play" }),
        "cursor-pointer transition-colors hover:bg-sunshine-yellow-600",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <Icon
        name="play-arrow"
        size="lg"
        filled
        className="text-deep-sea-blue-500"
      />
    </button>
  ),
);

PlayButton.displayName = "PlayButton";

export { PlayButton };
